import { Alert } from 'antd';
import React, { FunctionComponent, useCallback, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { RefFormHandler } from '../../pages/orders/details';
import { useAuthStore } from '../../store/auth-store';
import { FieldType } from '../../types';
import { errorMessage } from '../../utils';
import {
  validatePassword,
  validatePasswordMessage,
} from '../../utils/validators';
import Form from '../Form';

/**
 * ChangePasswordForm props
 */
interface Props {
  value?: string;
}

/**
 * ChangePasswordForm Component
 */
const ChangePasswordForm: FunctionComponent<Props> = () => {
  const formRef = useRef<RefFormHandler>(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { hideResetCode = false, email } = location.state as {
    hideResetCode?: boolean;
    email: string;
  };
  const { confirmPasswordReset, confirmPasswordResetError, isLoading, signIn } =
    useAuthStore();
  const [formFields, setFormFields] = useState([]);

  const onSubmitCapture = useCallback(
    async (data: unknown) => {
      const { newPassword, resetCode } = data as {
        newPassword: string;
        resetCode: string;
      };
      try {
        await confirmPasswordReset({
          username: email,
          newPassword,
          code: resetCode,
        });

        navigate('/auth/login');
      } catch (err) {
        setError(errorMessage(err));
      }
    },
    [confirmPasswordReset, navigate, signIn, email],
  );

  const fields: any[] = [];

  // Add resetCode field only if hideResetCode is false
  if (!hideResetCode) {
    fields.push({
      key: 'resetCode',
      required: true,
      inputProps: { autoComplete: 'off' },
    });
  }
  fields.push(
    {
      key: 'newPassword',
      type: FieldType.Password,
      rules: {
        validate(val) {
          if (!validatePassword(val)) {
            return validatePasswordMessage;
          }
          return undefined;
        },
      },
      inputProps: {
        autoComplete: 'off',
      },
    },
    {
      key: 'confirmPassword',
      type: FieldType.Password,
      rules: {
        validate: val => {
          if (val !== formRef.current?.getValues('newPassword')) {
            return 'Password should be same.';
          }
          return undefined;
        },
      },
      inputProps: {
        autoComplete: 'off',
      },
    },
  );
  return (
    <div>
      {confirmPasswordResetError || error ? (
        <Alert
          type="error"
          message={confirmPasswordResetError || error}
          className="mb-2"
        />
      ) : null}
      <Form
        ref={formRef}
        formProps={{ onSubmitCapture }}
        submitButtonProps={{
          loading: isLoading,
          disabled: isLoading,
        }}
        defaultValues={{ code: '' }}
        fields={fields}
      />
    </div>
  );
};

export default ChangePasswordForm;
